import { useState } from 'react';
import { AlertTriangle, Clock3 } from 'lucide-react';
import { toast } from 'sonner';
import { AddressAutocomplete } from '@/components/AddressAutocomplete';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { CreateRiderRideRequestPayload } from '@/types';

interface RiderRideRequestFormProps {
  onSubmit: (payload: CreateRiderRideRequestPayload) => Promise<void>;
  isSubmitting?: boolean;
}

const emptyForm: CreateRiderRideRequestPayload = {
  pickupLocation: '',
  destinationName: '',
  date: '',
  startTime: '',
  endTime: '',
  isEmergency: false,
};

export const RiderRideRequestForm = ({ onSubmit, isSubmitting = false }: RiderRideRequestFormProps) => {
  const [form, setForm] = useState(emptyForm);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.pickupLocation.trim() || !form.destinationName.trim() || !form.date || !form.startTime || !form.endTime) {
      toast.error('Add your pickup, destination, date, and available time window.');
      return;
    }

    const start = new Date(`${form.date}T${form.startTime}:00`);
    const end = new Date(`${form.date}T${form.endTime}:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
      toast.error('Your available-until time must be after your available-from time.');
      return;
    }

    await onSubmit(form);
    setForm(emptyForm);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <label className="text-sm font-medium">Pickup location</label>
        <AddressAutocomplete
          value={form.pickupLocation}
          onChange={(pickupLocation) => setForm((current) => ({ ...current, pickupLocation }))}
          placeholder="Where should the driver pick you up?"
          required
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Destination</label>
        <AddressAutocomplete
          value={form.destinationName}
          onChange={(destinationName) => setForm((current) => ({ ...current, destinationName }))}
          placeholder="Where do you need to go?"
          required
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Date</label>
        <Input
          type="date"
          value={form.date}
          min={new Date().toISOString().slice(0, 10)}
          onChange={(event) => setForm((current) => ({ ...current, date: event.target.value }))}
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <label className="text-sm font-medium">Available from</label>
          <Input type="time" value={form.startTime} onChange={(event) => setForm((current) => ({ ...current, startTime: event.target.value }))} required />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Available until</label>
          <Input type="time" value={form.endTime} onChange={(event) => setForm((current) => ({ ...current, endTime: event.target.value }))} required />
        </div>
      </div>

      <div className={cn('flex items-center justify-between rounded-xl border p-3 transition-colors', form.isEmergency ? 'border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950/30' : 'border-border bg-muted/30')}>
        <div className="flex items-center gap-3">
          <div className={cn('rounded-lg p-2', form.isEmergency ? 'bg-red-500 text-white' : 'bg-muted text-muted-foreground')}>
            {form.isEmergency ? <AlertTriangle className="h-4 w-4" /> : <Clock3 className="h-4 w-4" />}
          </div>
          <div>
            <p className="text-sm font-semibold">Emergency ride request</p>
            <p className="text-xs text-muted-foreground">Highlights this request to available drivers.</p>
          </div>
        </div>
        <Switch checked={form.isEmergency} onCheckedChange={(isEmergency) => setForm((current) => ({ ...current, isEmergency }))} />
      </div>
      {form.isEmergency && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-950/30 dark:text-red-300">
          CollegeRyde is not an emergency service. If you are in immediate danger, call 911.
        </p>
      )}

      <Button type="submit" disabled={isSubmitting} className={cn('h-12 w-full rounded-xl', form.isEmergency && 'bg-red-600 hover:bg-red-700')}>
        {isSubmitting ? 'Posting request…' : form.isEmergency ? 'Post emergency request' : 'Post ride request'}
      </Button>
    </form>
  );
};
